import { FormValues } from '../../../components/Store/reducers/formReducer/types'
import { AgPrefillResponse, RegistrationResponse } from './types'
import { formatters } from './formatters'
import { getFbclid, API, apiFetch } from '../shared/api'

// DEV-3482: 照合は1問目の「次へ」を押さえて待つため、応答が返らない回線だと
// スピナーのまま前進できなくなる。失敗時は転記なしで進む設計なので待つ意味が無く、
// 数秒で打ち切る（AbortErrorも呼び出し側のcatchで通常フローへ倒れる）
const AG_PREFILL_TIMEOUT_MS = 5000

/**
 * DEV-3255: 入力されたメールアドレスがAG会員のものかを照合する。
 * 通信に失敗したときは転記なし（通常フロー）として扱いたいので、呼び出し側で握り潰す前提。
 *
 * @param email 1問目で入力されたメールアドレス
 * @returns 転記対象でなければ prefill が null のレスポンス
 */
export const checkAgPrefill = async (
    email: string
): Promise<AgPrefillResponse> => {
    // AbortSignal.timeout はiOS Safari 16未満とjsdomに無いため、AbortControllerで打ち切る
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), AG_PREFILL_TIMEOUT_MS)

    try {
        return await apiFetch<AgPrefillResponse>(API.ENDPOINTS.AG_PREFILL, {
            method: 'POST',
            body: JSON.stringify({ user: { email } }),
            signal: controller.signal,
        })
    } finally {
        clearTimeout(timer)
    }
}

export const submitRegistration = async (
    data: FormValues
): Promise<RegistrationResponse> => {
    const csrfToken = document.querySelector<HTMLMetaElement>(
        'meta[name="csrf-token"]'
    )?.content
    if (!csrfToken) {
        throw new Error('CSRF token not found')
    }

    try {
        const formattedData = formatters(data)
        const fbclid = getFbclid()

        const response = await fetch(API.ENDPOINTS.SIGNUP, {
            method: 'POST',
            headers: {
                'Content-Type': API.HEADERS.CONTENT_TYPE,
                Accept: API.HEADERS.CONTENT_TYPE,
                'X-CSRF-Token': csrfToken,
            },

            body: JSON.stringify({
                user: formattedData,
                fbclid,
                // DEV-3255: 転記で電話番号ステップを飛ばした経路の合図。サーバはこれが
                // 送信するメールアドレスと一致したときだけ、空のtelをAG会員の番号で補う
                // eslint-disable-next-line camelcase -- Railsのparams名に合わせる
                ag_prefill_applied_email: data.agPrefillAppliedEmail || null,
            }),
        })

        if (!response.ok) {
            const contentType = response.headers.get('content-type') || ''
            if (contentType.includes('text/plain')) {
                const errorMessage = await response.text()
                return Promise.reject(new Error(errorMessage))
            }
            return Promise.reject(
                new Error(`HTTP error! status: ${response.status}`)
            )
        }

        const responseData = await response.json()

        if (responseData.status === 'success' && responseData.location) {
            window.location.href = responseData.location
        }
        return responseData
    } catch (error: unknown) {
        if (error instanceof Error) {
            throw new Error(`Registration failed: ${error.message}`)
        }
        throw new Error('Registration failed: Unknown error')
    }
}
